import { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useImportRerun, useImports } from "../../api/queries";
import type { ImportDiffDto, ImportItem } from "../../api/types";
import { errorText } from "../../api/common";
import { DateDisplay } from "../../components/DateDisplay";
import { DateTimeDisplay } from "../../components/DateTimeDisplay";
import { ImportDetailsModal } from "../../components/ImportDetailsModal";
import { ImportDiffModal } from "../../components/ImportDiffModal";

/**
 * After an importer change, imports processed under the old importer version
 * are flagged `needsRerun` by the backend. Surface them on the dashboard as a
 * pending task. Re-run is the row's primary action — one click produces the
 * diff to review, no detour through the details modal — while the name still
 * opens the full details view.
 */
export const ImportsNeedingRerun = () => {
  const { data: imports } = useImports();
  const importRerun = useImportRerun();
  const [detailsItem, setDetailsItem] = useState<ImportItem | null>(null);
  const [review, setReview] = useState<{
    importId: string;
    diff: ImportDiffDto;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stale = (imports ?? []).filter((i) => i.needsRerun);

  const handleRerun = (imp: ImportItem) => {
    setError(null);
    importRerun.mutate(imp.id, {
      onSuccess: (diff) => setReview({ importId: imp.id, diff }),
      onError: (e) => setError(`Re-run failed: ${errorText(e)}`),
    });
  };

  return (
    <>
      {stale.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {stale.length === 1
              ? "1 import needs a re-run"
              : `${stale.length} imports need a re-run`}
          </AlertTitle>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            The importer changed since these were imported. Re-run each to
            process its original file again — the diff shows what that changes.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {stale.map((imp) => (
              <Box
                key={imp.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setDetailsItem(imp)}
                    underline="hover"
                    sx={{ textAlign: "left" }}
                  >
                    {imp.importerName}
                  </Link>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: "text.secondary", ml: 1 }}
                  >
                    Imported <DateTimeDisplay date={imp.dateImported} />
                    {imp.firstTransactionDate && imp.lastTransactionDate && (
                      <>
                        {" · Transactions "}
                        <DateDisplay date={imp.firstTransactionDate} />
                        {" – "}
                        <DateDisplay date={imp.lastTransactionDate} />
                      </>
                    )}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={importRerun.isPending}
                  startIcon={
                    importRerun.isPending &&
                    importRerun.variables === imp.id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : undefined
                  }
                  onClick={() => handleRerun(imp)}
                >
                  Re-run
                </Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}
      {detailsItem && (
        <ImportDetailsModal
          key={detailsItem.id}
          open
          onClose={() => setDetailsItem(null)}
          importItem={detailsItem}
        />
      )}
      {review && (
        <ImportDiffModal
          open
          onClose={() => setReview(null)}
          importId={review.importId}
          preloadedDiff={review.diff}
        />
      )}
    </>
  );
};
